import * as PIXI from "pixi.js";
import { sound } from "@pixi/sound";

/**
 * アセット管理用クラス
 */
class AsseetsManager {
  // ---------------------------------------------------------------------------------------------------------
  // --	VARS
  // ---------------------------------------------------------------------------------------------------------

  //シングルトンインスタンス
  private static _instance: AsseetsManager;

  //再生中のサウンドを保持するMAP
  private _playingSounds: Map<any, any> = new Map();

  //ミュートしているかどうかのフラグ
  private _isMute: boolean = false;
  public get isMute(): boolean {
    return this._isMute;
  }

  // ---------------------------------------------------------------------------------------------------------
  // --	AsseetsManager
  // ---------------------------------------------------------------------------------------------------------

  /**
   * コンストラクタ
   */
  private constructor() {
    document.addEventListener(
      "visibilitychange",
      () => {
        if (document.visibilityState === "visible") {
          console.log("コンテンツが表示された");
          this.soundResume();
        }
        if (document.visibilityState === "hidden") {
          console.log("コンテンツがバックグラウンドになった");
        }
      },
      false
    );
  }

  /** インスタンスの取得 */
  public static get instance(): AsseetsManager {
    if (!this._instance) {
      this._instance = new AsseetsManager();
    }

    // 生成済みのインスタンスを返す
    return this._instance;
  }

  // ---------------------------------------------------------------------------------------------------------
  // --	読み込み
  // ---------------------------------------------------------------------------------------------------------

  async loadBundle(name, assets) {
    PIXI.Assets.addBundle(name, assets);
    const result = await PIXI.Assets.loadBundle(name);
    console.log(result);
  }

  // ---------------------------------------------------------------------------------------------------------
  // --	画像
  // ---------------------------------------------------------------------------------------------------------

  /**
   * テクスチャ取得
   */
  getTexture(name: string, antialiasing: boolean = false): PIXI.Texture {
    if (PIXI.Assets.cache.has(name)) {
      const texture: PIXI.Texture = PIXI.Assets.get(name);
      texture.baseTexture.scaleMode = antialiasing
        ? PIXI.SCALE_MODES.LINEAR
        : PIXI.SCALE_MODES.NEAREST;
      return texture;
    }

    return null;
  }

  /**
   * アニメーション取得
   */
  getAnimation(
    label: string,
    antialiasing: boolean = false
  ): Array<PIXI.Texture> {
    // const bundle = PIXI.Assets.get(key)
    // const animations = bundle ? bundle.animations : null
    // const frames = animations ? animations[label] : null

    // console.log("==================");
    // console.log("getAnimation:", label);

    const frames: Array<PIXI.Texture> = new Array<PIXI.Texture>();
    let index: number = -1;
    let loop: boolean = true;
    while (loop) {
      index++;
      let txLabel: string = label + index.toString().padStart(4, "0");
      const texture: PIXI.Texture = this.getTexture(txLabel, antialiasing);
      if (texture) {
        frames.push(texture);
      } else {
        loop = false;
      }
    }

    return frames;
  }

  /**
   * スプライト生成
   */
  createSpriteByName(name: string, antialiasing: boolean = false): PIXI.Sprite {
    return PIXI.Sprite.from(this.getTexture(name, antialiasing));
  }

  /**
   * アニメーション作成
   */
  createMovieClipFromAnimation(
    label: string,
    antialiasing: boolean = false
  ): PIXI.AnimatedSprite {
    const frames: Array<PIXI.Texture> = this.getAnimation(label, antialiasing);
    if (frames) {
      return this.createMovieClip(frames);
    } else {
      throw new Error("指定されたラベル名のアニメーションがありません");
    }
  }

  /**
   * ムービークリップ生成
   */
  createMovieClip(frames: Array<PIXI.Texture>): PIXI.AnimatedSprite {
    return new PIXI.AnimatedSprite(frames);
  }

  // ---------------------------------------------------------------------------------------------------------
  // --	サウンド
  // ---------------------------------------------------------------------------------------------------------

  playSound(name: string, option: any = null, callback: Function = null): void {
    const s = PIXI.Assets.get(name);
    if (!s) {
      console.log(name + "が見つかりませんでした");
      return;
    }

    //サウンド再開
    this.soundResume();

    //同じ音はかぶらないようにする
    this.stopSound(name);

    if (!option) {
      option = { volume: 0.1 };
    }

    //再生
    const instance = s.play(option);
    this._playingSounds.set(s, instance);
    instance.on("end", () => {
      this.stopSound(name);
      if (callback) callback();
    });
  }

  stopSound(name: string): void {
    const s = PIXI.Assets.get(name);
    if (!s) {
      console.log(name + "が見つかりませんでした");
      return;
    }

    if (this._playingSounds.has(s)) {
      const instance = this._playingSounds.get(s);
      instance.off("end");
      instance.stop();
      this._playingSounds.delete(s);
    }
  }

  stopAllSounds(): void {
    //全ての音をとめる
    sound.stopAll();
  }

  stop__playingSounds(): void {
    this._playingSounds.forEach((instance, s) => {
      instance.off("end");
      instance.stop();
      this._playingSounds.delete(s);
    });
  }

  soundMute(bool: boolean): void {
    this._isMute = bool;

    if (this._isMute) {
      sound.muteAll();
    } else {
      sound.unmuteAll();
    }
  }

  soundResume(): void {
    //@ts-ignore
    // console.log("sound.context._ctx.state:", sound.context._ctx.state);

    //@ts-ignore
    const ctx: any = sound.context._ctx;
    if (
      (ctx.state == "interrupted" || ctx.state == "suspended") &&
      document.visibilityState === "visible"
    )
      ctx.resume();
  }

  // ---------------------------------------------------------------------------------------------------------
}

export default AsseetsManager;
